// CSVMatchUpload.jsx - CSV Upload Component for Matching Registered Users
import React, { useState, useRef } from 'react';
import { Box, Typography, Button, Alert, Tooltip, IconButton } from '@mui/material';
import { CloudUpload, InfoOutlined } from '@mui/icons-material';
import Papa from 'papaparse';
import Swal from 'sweetalert2';

const CSVMatchUpload = ({ registeredList, onMatch, audienceType,isLoading }) => {
    const [matchedCount, setMatchedCount] = useState(0);
    const [unmatchedCount, setUnmatchedCount] = useState(0);
    const fileInputRef = useRef(null);

    const validatePhoneNumber = (phone) => {
        const cleaned = String(phone).replace(/\D/g, '');
        
        if (cleaned.length === 10) {
            return '91' + cleaned;
        } else if (cleaned.length === 12 && cleaned.startsWith('91')) {
            return cleaned;
        } else if (cleaned.length === 13 && cleaned.startsWith('091')) {
            return cleaned.substring(1);
        }
        
        return null;
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        Papa.parse(file, {
            complete: (results) => {
                const uploadedNumbers = [];
                
                results.data.forEach((row) => {
                    let phoneValue = row.phone || row.phoneNumber || row.mobileNumber || row.mobile || row[0];
                    
                    if (phoneValue) {
                        const validated = validatePhoneNumber(phoneValue);
                        if (validated && !uploadedNumbers.includes(validated)) {
                            uploadedNumbers.push(validated);
                        }
                    }
                });

                // Match with registered list
                const matched = [];
                const unmatched = [];

                uploadedNumbers.forEach(number => {
                    const found = registeredList.find(user => {
                        const userNumber = validatePhoneNumber(user.mobileNumber);
                        return userNumber === number;
                    });

                    if (found) {
                        matched.push(found);
                    } else {
                        unmatched.push(number);
                    }
                });

                setMatchedCount(matched.length);
                setUnmatchedCount(unmatched.length);

                // Show results
                if (matched.length > 0) {
                    Swal.fire({
                        title: '<strong style="font-size: 1rem;">CSV Match Results</strong>',
                        icon: 'success',
                        html: `
                            <div style="text-align: left; font-size: 0.75rem;">
                                <p style="font-size: 0.8rem;"><strong>✅ Matched:</strong> ${matched.length} ${audienceType}(s)</p>
                                <p style="font-size: 0.8rem;"><strong>❌ Unmatched:</strong> ${unmatched.length} numbers</p>
                                <p style="font-size: 0.75rem; color: #666; margin-top: 10px;">
                                    The matched ${audienceType}s will be selected automatically.
                                </p>
                            </div>
                        `,
                        confirmButtonText: 'Apply Selection',
                        confirmButtonColor: '#1976d2',
                        showCancelButton: true,
                        cancelButtonText: 'Cancel',
                        didOpen: () => {
                            const confirmButton = Swal.getConfirmButton();
                            if (confirmButton) {
                                confirmButton.style.fontSize = '0.75rem';
                                confirmButton.style.padding = '6px 16px';
                            }
                            const cancelButton = Swal.getCancelButton();
                            if (cancelButton) {
                                cancelButton.style.fontSize = '0.75rem';
                                cancelButton.style.padding = '6px 16px';
                            }
                            const icon = Swal.getIcon();
                            if (icon) {
                                icon.style.width = '50px';
                                icon.style.height = '50px';
                            }
                        }
                    }).then((result) => {
                        if (result.isConfirmed) {
                            onMatch(matched);
                        }
                    });
                } else {
                    Swal.fire({
                        title: '<strong style="font-size: 1rem;">No Matches Found</strong>',
                        icon: 'warning',
                        html: `
                            <div style="text-align: left; font-size: 0.75rem;">
                                <p style="font-size: 0.8rem;">No registered ${audienceType}s matched the uploaded phone numbers.</p>
                                <p style="font-size: 0.75rem; color: #666; margin-top: 10px;">
                                    Please check:
                                </p>
                                <ul style="font-size: 0.75rem; margin-left: 20px; color: #666;">
                                    <li>Phone numbers are correct</li>
                                    <li>Users are registered in the system</li>
                                    <li>Filters are not too restrictive</li>
                                </ul>
                            </div>
                        `,
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#1976d2',
                        didOpen: () => {
                            const confirmButton = Swal.getConfirmButton();
                            if (confirmButton) {
                                confirmButton.style.fontSize = '0.75rem';
                                confirmButton.style.padding = '6px 16px';
                            }
                            const icon = Swal.getIcon();
                            if (icon) {
                                icon.style.width = '50px';
                                icon.style.height = '50px';
                            }
                        }
                    });
                }

                // Reset file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            },
            header: true,
            skipEmptyLines: true,
            error: (error) => {
                console.error('CSV Parse Error:', error);
                Swal.fire({
                    title: 'Error',
                    text: `Failed to parse CSV: ${error.message}`,
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        });
    };

    const showInstructions = () => {
        Swal.fire({
            title: '<strong style="font-size: 1rem;">CSV Upload Instructions</strong>',
            icon: 'info',
            html: `
                <div style="text-align: left;">
                    <p style="font-size: 0.8rem; margin-bottom: 10px;"><strong>📱 Purpose:</strong> <span style="font-size: 0.75rem;">Select registered ${audienceType}s by uploading phone numbers</span></p>
                    
                    <p style="font-size: 0.8rem; margin-bottom: 5px;"><strong>📄 How it works:</strong></p>
                    <ul style="margin-left: 20px; font-size: 0.75rem;">
                        <li>Upload CSV with phone numbers</li>
                        <li>System matches with filtered registered ${audienceType}s</li>
                        <li>Matched ${audienceType}s are automatically selected</li>
                    </ul>
                    
                    <p style="font-size: 0.8rem; margin-bottom: 5px; margin-top: 10px;"><strong>✅ Requirements:</strong></p>
                    <ul style="margin-left: 20px; font-size: 0.75rem;">
                        <li>CSV column: 'phoneNumber', 'phone', 'mobile', or 'mobileNumber'</li>
                        <li>Phone numbers: exactly 10 digits</li>
                        <li>${audienceType}s must be in filtered list</li>
                    </ul>
                </div>
            `,
            showCloseButton: true,
            confirmButtonText: 'Got it!',
            confirmButtonColor: '#1976d2',
            didOpen: () => {
                const confirmButton = Swal.getConfirmButton();
                if (confirmButton) {
                    confirmButton.style.fontSize = '0.75rem';
                    confirmButton.style.padding = '6px 16px';
                }
                const icon = Swal.getIcon();
                if (icon) {
                    icon.style.width = '50px';
                    icon.style.height = '50px';
                }
            }
        });
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                    Or upload CSV to select registered {audienceType}s
                </Typography>
                <Tooltip title="View Instructions">
                    <IconButton 
                        size="small" 
                        onClick={showInstructions}
                        sx={{ 
                            bgcolor: 'primary.50',
                            '&:hover': { bgcolor: 'primary.100' }
                        }}
                    >
                        <InfoOutlined fontSize="small" color="primary" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:'center', gap: 1, mb: 1 }}>
                <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    size="small"
                    disabled={isLoading}
                    // fullWidth
                >
                    Upload CSV
                    <input
                        type="file"
                        hidden
                        accept=".csv"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                    />
                </Button>
            </Box>
            {matchedCount > 0 && (
                <Alert severity="success" sx={{ mt: 1, py: 0.5 }}>
                    <Typography variant="caption">
                        Last upload: {matchedCount} matched, {unmatchedCount} unmatched
                    </Typography>
                </Alert>
            )}
        </Box>
    );
};

export default CSVMatchUpload;
